import { faBellOn, faBellSlash } from '@fortawesome/pro-regular-svg-icons';
import {
    faBellOn as faBellOnSolid,
    faBellSlash as faBellSlashSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
    HardwareHealthNotificationSubscriptionResponse,
    HardwareHealthNotificationSubscriptionUpdateRequest,
    HardwareHealthNotificationTriggerRequestChild,
    HardwareNotificationSubscriptionResponse,
    HardwareNotificationSubscriptionUpdateRequest,
    IndicatorNotificationSubscriptionResponse,
    IndicatorNotificationSubscriptionUpdateRequest,
    MachineHealthNotificationSubscriptionResponse,
    MachineHealthNotificationSubscriptionUpdateRequest,
    MachineHealthNotificationTriggerRequestChild,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { NotificationSubscription } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { getNotificationTypeDisplayName } from 'pages/Settings/NotificationSubscriptions/shared';
import { tokens } from 'styles/tokens';

type NotificationSubscriptionToggleButtonProps = {
    subscription: NotificationSubscription;
    onToggle: () => void;
};

type NotificationSubscriptionToggleResponse = Promise<
    | IndicatorNotificationSubscriptionResponse
    | HardwareNotificationSubscriptionResponse
    | MachineHealthNotificationSubscriptionResponse
    | HardwareHealthNotificationSubscriptionResponse
>;

export function NotificationSubscriptionToggleButton({
    subscription,
    onToggle,
}: NotificationSubscriptionToggleButtonProps) {
    const { showToast } = useNotificationContext();

    const isSilenced = subscription.IsSilenced;

    const { mutate: toggleNotificationSubscription, isPending: isToggling } = useMutation(
        {
            meta: { name: 'toggle-notification-subscription' },
            mutationFn: (): NotificationSubscriptionToggleResponse => {
                switch (subscription.discriminator) {
                    case 'Indicator':
                        return sdClient.updateIndicatorNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new IndicatorNotificationSubscriptionUpdateRequest({
                                IsSilenced: !isSilenced,
                                UnacknowledgedPeriod: subscription.UnacknowledgedPeriod,
                                NotificationTriggers: subscription.NotificationTriggers,
                            }),
                        });
                    case 'Hardware':
                        return sdClient.updateHardwareNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new HardwareNotificationSubscriptionUpdateRequest({
                                IsSilenced: !isSilenced,
                                NotificationTriggers: subscription.NotificationTriggers,
                            }),
                        });
                    case 'MachineHealth':
                        return sdClient.updateMachineHealthNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new MachineHealthNotificationSubscriptionUpdateRequest({
                                IsSilenced: !isSilenced,
                                NotificationTriggers:
                                    subscription.NotificationTriggers.map(
                                        (nt) =>
                                            new MachineHealthNotificationTriggerRequestChild(
                                                {
                                                    NotificationTriggerId:
                                                        nt.NotificationTriggerId,
                                                    MachineHealthEventType:
                                                        nt.MachineHealthEventType,
                                                    IsSdConnectPushEnabled:
                                                        nt.IsSdConnectPushEnabled,
                                                    IsEmailEnabled: nt.IsEmailEnabled,
                                                    IsSmsEnabled: nt.IsSmsEnabled,
                                                },
                                            ),
                                    ),
                            }),
                        });
                    case 'HardwareHealth':
                        return sdClient.updateNotificationSubscription({
                            subscriptionId: subscription.Id,
                            body: new HardwareHealthNotificationSubscriptionUpdateRequest(
                                {
                                    IsSilenced: !isSilenced,
                                    NotificationTriggers:
                                        subscription.NotificationTriggers.map(
                                            (nt) =>
                                                new HardwareHealthNotificationTriggerRequestChild(
                                                    {
                                                        FaultTypeId: nt.FaultType.Id,
                                                        IsSdConnectPushEnabled:
                                                            nt.IsSdConnectPushEnabled,
                                                        IsEmailEnabled: nt.IsEmailEnabled,
                                                        IsSmsEnabled: nt.IsSmsEnabled,
                                                    },
                                                ),
                                        ),
                                },
                            ),
                        });
                    default:
                        throw new Error('invalid notification type');
                }
            },

            onSuccess: () => {
                onToggle();
                showToast({
                    content: `Successfully ${
                        isSilenced ? 'unsilenced' : 'silenced'
                    } ${getNotificationTypeDisplayName(
                        subscription,
                    )} notification subscription`,
                    type: 'success',
                });
            },

            onError: () => {
                showToast({
                    content: `Failed to ${
                        isSilenced ? 'unsilence' : 'silence'
                    } ${getNotificationTypeDisplayName(
                        subscription,
                    )} notification subscription`,
                    type: 'error',
                });
            },
        },
    );

    return (
        <HoverIconButton
            title={`${
                isSilenced ? 'Unsilence' : 'Silence'
            } ${getNotificationTypeDisplayName(subscription)} notification subscription`}
            icon={isSilenced ? faBellSlash : faBellOn}
            hoverIcon={isSilenced ? faBellOnSolid : faBellSlashSolid}
            color={tokens.text.secondary}
            hoverColor={tokens.primary.default}
            onClick={() => toggleNotificationSubscription()}
            disabled={isToggling}
        />
    );
}
